import { useState, useRef, useEffect, useMemo, useCallback } from "react";
export { signal, type ReactiveSignal, type ReactiveSignal as Signal } from "@decocms/blocks/sdk/signal";

/** Run a function immediately. Kept for legacy module-level side effects. */
export function effect(fn: () => void | (() => void)): () => void {
  const cleanup = fn();
  return typeof cleanup === "function" ? cleanup : () => {};
}

/**
 * React shim for @preact/signals' useSignal.
 * Returns a mutable ref-like object with a .value property that triggers re-renders.
 */
export function useSignal<T>(initialValue: T): { value: T } {
  const [value, setValue] = useState<T>(initialValue);
  const ref = useRef(value);
  ref.current = value;
  return useMemo(
    () => ({
      get value() { return ref.current; },
      set value(v: T) {
        ref.current = v;
        setValue(v);
      },
    }),
    [],
  );
}

/**
 * React shim for @preact/signals' useComputed.
 * Re-evaluates when deps change (but since we don't track signals, it runs every render).
 */
export function useComputed<T>(compute: () => T): { readonly value: T } {
  const [value, setValue] = useState<T>(compute);
  useEffect(() => { setValue(compute()); });
  return useMemo(() => ({ get value() { return value; } }), [value]);
}

/**
 * React shim for @preact/signals' useSignalEffect.
 * Runs the callback as a useEffect (no automatic signal tracking).
 */
export function useSignalEffect(cb: () => void | (() => void)): void {
  useEffect(cb);
}
